<template>
  <div class="class-report-container">
    <!-- 页面头部 -->
    <div class="report-header">
      <div class="header-content">
        <h1 class="report-title">课程学习报告</h1>
        <div class="report-subtitle">详细的学习进度与成果展示</div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-loading text="正在加载报告数据..." />
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-content">
        <i class="el-icon-warning-outline error-icon"></i>
        <h3>加载失败</h3>
        <p>{{ error }}</p>
        <el-button type="primary" @click="loadReportData">重新加载</el-button>
      </div>
    </div>

    <!-- 报告内容 -->
    <div v-else-if="reportData" class="report-content">
      <!-- 课程基本信息 -->
      <div class="course-info-card">
        <div class="card-header">
          <h2>课程信息</h2>
        </div>
        <div class="card-content">
          <div class="info-grid">
            <div class="info-item">
              <span class="label">课程学科：</span>
              <span class="value">{{ reportData.subjectName }}</span>
            </div>
            <div class="info-item">
              <span class="label">预定时长：</span>
              <span class="value">{{ reportData.classHour }} 小时</span>
            </div>
            <div class="info-item">
              <span class="label">开始时间：</span>
              <span class="value">{{ formatTime(reportData.beginTime) }}</span>
            </div>
            <div class="info-item" v-if="reportData.endTime">
              <span class="label">结束时间：</span>
              <span class="value">{{ formatTime(reportData.endTime) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 学习进度统计 -->
      <div class="progress-card">
        <div class="card-header">
          <h2>学习统计</h2>
        </div>
        <div class="card-content">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">{{ reportData.reportVos.length }}</div>
              <div class="stat-label">学习模块</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ totalTime.toFixed(2) }}</div>
              <div class="stat-label">总学习时长(分钟)</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ averageTime.toFixed(2) }}</div>
              <div class="stat-label">平均时长(分钟)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 学习详情 -->
      <div class="details-card">
        <div class="card-header">
          <h2>学习详情</h2>
        </div>
        <div class="card-content">
          <div class="course-list">
            <div 
              v-for="(item, index) in reportData.reportVos" 
              :key="index" 
              class="course-item"
            >
              <div class="course-index">{{ index + 1 }}</div>
              <div class="course-info">
                <h3 class="course-name">{{ item.businessName }}</h3>
                <p class="course-point">知识点：{{ item.pointName }}</p>
              </div>
              <div class="course-time">
                <span class="time-value">{{ item.courseLongTime.toFixed(2) }}</span>
                <span class="time-unit">分钟</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页面底部 -->
    <div class="report-footer">
      <div class="footer-content">
        <p>© 夺分派学习平台 - 专业的在线教育服务</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ClassReport',
  data() {
    return {
      loading: false,
      error: null,
      reportData: null,
      classId: null
    }
  },
  computed: {
    totalTime() {
      if (!this.reportData || !this.reportData.reportVos) return 0
      return this.reportData.reportVos.reduce((sum, item) => sum + item.courseLongTime, 0)
    },
    averageTime() {
      if (!this.reportData || !this.reportData.reportVos || this.reportData.reportVos.length === 0) return 0
      return this.totalTime / this.reportData.reportVos.length
    }
  },
  created() {
    this.getClassIdFromRoute()
    if (this.classId) {
      this.loadReportData()
    } else {
      this.error = '缺少课程ID参数'
    }
  },
  methods: {
    getClassIdFromRoute() {
      // 从hash路由中获取classId参数
      const hash = window.location.hash
      const urlParams = new URLSearchParams(hash.split('?')[1])
      this.classId = urlParams.get('classId')
    },
    async loadReportData() {
      this.loading = true
      this.error = null
      
      try {
        const res = await this.$api.getClassReport(this.classId);
        if (res.data.success) {
          this.reportData = res.data.data
        } else {
          this.error = res.data.errMsg || '获取报告数据失败'
        }
      } catch (err) {
        this.error = '网络请求失败，请稍后重试'
        console.error('获取课程报告失败:', err)
      } finally {
        this.loading = false
      }
    },
    formatTime(timeString) {
      if (!timeString) return '-'
      const date = new Date(timeString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.class-report-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Helvetica Neue', Arial, sans-serif;
}

.report-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 40px 0;
  text-align: center;
  color: white;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.report-title {
  font-size: 3rem;
  font-weight: 300;
  margin: 0 0 10px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.report-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  font-weight: 300;
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.error-content {
  text-align: center;
  color: white;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-content h3 {
  font-size: 1.5rem;
  margin: 0 0 10px 0;
}

.error-content p {
  margin: 0 0 20px 0;
  opacity: 0.8;
}

.report-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.course-info-card,
.progress-card,
.details-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 30px;
}

.card-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
}

.card-content {
  padding: 30px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.info-item .label {
  font-weight: 600;
  color: #666;
  margin-right: 10px;
  min-width: 80px;
}

.info-item .value {
  color: #333;
  font-weight: 500;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border-radius: 12px;
  color: white;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 500;
}

.course-list {
  space-y: 15px;
}

.course-item {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.course-item:hover {
  background: #e9ecef;
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.course-index {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 20px;
  flex-shrink: 0;
}

.course-info {
  flex: 1;
}

.course-name {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.course-point {
  margin: 0;
  color: #666;
  font-size: 0.9rem;
}

.course-time {
  text-align: right;
  flex-shrink: 0;
}

.time-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
}

.time-unit {
  display: block;
  font-size: 0.8rem;
  color: #999;
  margin-top: 2px;
}

.report-footer {
  background: rgba(0, 0, 0, 0.1);
  color: white;
  text-align: center;
  padding: 30px 20px;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-content p {
  margin: 0;
  opacity: 0.8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .report-title {
    font-size: 2rem;
  }
  
  .report-subtitle {
    font-size: 1rem;
  }
  
  .card-content {
    padding: 20px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .course-item {
    flex-direction: column;
    text-align: center;
  }
  
  .course-index {
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .course-time {
    margin-top: 15px;
  }
}
</style>